import { Injectable, inject } from '@angular/core';
import { HttpService } from './http.service';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { UserDetailDto } from '../dtos/user-detail.dto';
import { Role } from '../models/role.model';
import { Attachment } from '../models/attachment.model';
import { FormControl, FormGroup } from '@angular/forms';
import { SubscriptionService } from './subscription.service';
import { ConfigurationService } from './configuration.service';

const REMEMBERED_LOGIN_KEY = 'rememberedLogin';

export interface RememberedLogin {
  tenantSlug: string;
  username: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  httpService = inject(HttpService);
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);
  private configurationService = inject(ConfigurationService);

  private profilePic: any;

  public connetedUserSubject: BehaviorSubject<UserDetailDto> = new BehaviorSubject(this.setConnectedUserSubject);

  public set setConnectedUserSubject(value: UserDetailDto) {
    this.connetedUserSubject.next(value);
  }

  public get getProfilePic() {
    return this.profilePic;
  }

  public set setProfilePic(picture: any) {
    this.profilePic = picture;
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  saveTokenData(data: any, rememberMe: boolean) {
    this.removeTokenData();
    // Save tokenData to local storage
    sessionStorage.setItem('tokenData', JSON.stringify(data));
    // Save tokenData to session storage
    if (rememberMe) localStorage.setItem('tokenData', JSON.stringify(data));

    this.saveRememberMe(rememberMe);
  }

  saveRememberMe(value: boolean) {
    localStorage.setItem('rememberMe', JSON.stringify(value));
  }

  getRememberMe() {
    let stringData = localStorage.getItem('rememberMe');
    let value: boolean = false;
    if (stringData) value = JSON.parse(stringData);
    return value;
  }

  removeTokenData() {
    // Remove tokenData from local storage
    if (sessionStorage.getItem('tokenData') != undefined) sessionStorage.removeItem('tokenData');
    // Remove tokenData from ession storage
    if (localStorage.getItem('tokenData') != undefined) localStorage.removeItem('tokenData');
  }

  /**
   * The company slug and username of the last sign-in made with "remember me", used to prefill the form. The
   * password is deliberately not kept: that is the browser's password manager's job.
   */
  saveRememberedLogin(login: RememberedLogin) {
    localStorage.setItem(REMEMBERED_LOGIN_KEY, JSON.stringify(login));
  }

  getRememberedLogin(): RememberedLogin | undefined {
    try {
      const stringData = localStorage.getItem(REMEMBERED_LOGIN_KEY);
      return stringData ? JSON.parse(stringData) : undefined;
    } catch {
      return undefined;
    }
  }

  clearRememberedLogin() {
    localStorage.removeItem(REMEMBERED_LOGIN_KEY);
  }

  isAuthenticated() {
    let token = this.getToken();
    let isTokenExist = token != undefined && token != '';
    return isTokenExist;
  }

  public getCurrentUser(): UserDetailDto | undefined {
    let jsonData = undefined;

    // Check local storage
    let stringData = sessionStorage.getItem('user') ?? undefined;
    // Check session storage
    if (stringData == undefined) stringData = localStorage.getItem('user') ?? undefined;

    if (stringData != undefined && typeof stringData === 'string' && stringData != 'undefined' && stringData != '') {
      try {
        jsonData = JSON.parse(stringData);
        this.setConnectedUserSubject = jsonData;
      } catch (error) {
        // Handle the JSON parsing error
        console.error('Error parsing JSON:', error);
      }
    }

    return jsonData;
  }

  public saveUser(user: UserDetailDto, rememberMe: boolean) {
    this.setConnectedUserSubject = user;
    sessionStorage.setItem('user', JSON.stringify(user));
    if (rememberMe) localStorage.setItem('user', JSON.stringify(user));
  }

  public getUserRoles(): Role[] {
    let user: UserDetailDto | undefined = this.getCurrentUser();
    let roles: Array<Role> = [];
    if (user) {
      roles = user.roles!;
      return roles;
    } else return [];
  }

  public getTokenData(): any {
    let jsonData = undefined;
    // Check local storage
    let stringData = sessionStorage.getItem('tokenData') ?? undefined;
    // Check session storage
    if (stringData == undefined) stringData = localStorage.getItem('tokenData') ?? undefined;

    if (stringData != undefined && typeof stringData === 'string' && stringData != 'undefined' && stringData != '') {
      try {
        jsonData = JSON.parse(stringData);
      } catch (error) {
        // Handle the JSON parsing error
        console.error('Error parsing JSON:', error);
      }
    }

    return jsonData;
  }

  public getToken(): string | undefined {
    let token = undefined;
    let tokenData = this.getTokenData();
    if (tokenData) token = tokenData?.accessToken ?? undefined;
    return token;
  }

  public getRefreshToken(): string | undefined {
    let tokenData = this.getTokenData();
    let refreshToken = tokenData?.refreshToken ?? undefined;
    return refreshToken;
  }

  public hasRole(rl: string): boolean {
    let res: boolean = false;
    //Parse through authorities
    this.getUserRoles().forEach((role: Role) => {
      //Check if there is a match
      if (role.title == rl) res = true;
    });

    return res;
  }

  public checkRoles(roles: Array<Role>): boolean {
    let check: boolean = false;

    //Parse through authorities
    if (roles.length == 0 || roles == null) check = true;

    for (let role of this.getUserRoles()) {
      //Check if there is a match
      if (roles.some((r) => r == role.title)) {
        check = true;
        break;
      }
    }

    return check;
  }

  /*public hasPrivilege(privilege?: string): boolean {
    let exists: boolean = false;

    if (privilege == undefined || privilege == '') return true;

    for (let role of this.getUserRoles()) {
      for (let priv of role.privileges ?? []) {
        // Check if there is a match
        if (priv.title == privilege) {
          exists = true;
          break;
        }
      }
    };

    return exists;
  }*/

  public hasPrivilege(privilege?: string): boolean {
    // If no privilege is specified, return true
    if (!privilege) return true;

    // Check if any role has the specified privilege
    return this.getUserRoles().some((role) => role.privileges?.some((priv) => priv.title == privilege));
  }

  public hasAnyPrivilege(privileges?: string[]): boolean {
    // If no privileges are provided, return true
    if (!privileges || privileges.length === 0) return true;

    // Get all privileges from the user's roles
    const userPrivileges = this.getUserRoles().flatMap((role) => role.privileges?.map((priv) => priv.title) ?? []);

    // Check if any of the provided privileges exist in the user's privileges
    return privileges.some((privilege) => userPrivileges.includes(privilege));
  }

  /**
   * Whether the signed-in user holds a capability from `permissions.ts` - any one of its
   * privileges, as the API decides it.
   *
   * <p>Unlike {@link hasAnyPrivilege}, an empty list is refused rather than allowed: a
   * capability with no privileges is a mistake, and failing open would show the action to
   * everybody.
   */
  public can(capability: readonly string[]): boolean {
    if (!capability.length) return false;
    return this.hasAnyPrivilege([...capability]);
  }

  /**
   * This is the authentication function, it must return the token that will be used to send http requests
   */
  public login(formGroupValue: any): Promise<any> {
    let params = {
      tenantSlug: formGroupValue.tenantSlug,
      username: formGroupValue.username,
      password: formGroupValue.password,
      grantType: 'password',
      withRefreshToken: true,
      refreshToken: '',
    };
    return this.httpService.post('/v1.0/auth/token', params);
  }

  public refreshToken(): Promise<any> {
    let refreshToken: string | unknown = this.getRefreshToken();
    let params = {
      username: '',
      password: '',
      grantType: 'refreshToken',
      withRefreshToken: true,
      refreshToken: refreshToken,
    };
    // A failed refresh is handled by the caller (the token interceptor logs out once), so it must reject — and
    // without HttpService's error dialog, which would also wipe storage behind the interceptor's back.
    return this.httpService.post('/v1.0/auth/token', params, '', 'json', { skipErrorAlert: true }).then((data: any) => {
      this.saveTokenData(data, this.getRememberMe());
      return data;
    });
  }

  /**
   * What the app reads once per session and needs before the first screen: the company's plan
   * (which modules exist) and its settings. Both need the token, so on a fresh sign-in they can
   * only be read now - call it after saving the token and before navigating.
   */
  loadSessionContext(): Promise<void> {
    return Promise.all([this.subscriptionService.refresh(), this.configurationService.initializeConfigurations()]).then(
      () => undefined,
    );
  }

  logout() {
    this.subscriptionService.reset();
    this.configurationService.reset();
    this.removeTokenData();
    sessionStorage.removeItem('user');
    localStorage.removeItem('user');
    this.profilePic = null;
    localStorage.removeItem('token');
    // Several requests failing together each end up here; one navigation is enough.
    if (!this.router.url.startsWith('/auth/sign-in')) this.router.navigateByUrl('/auth/sign-in');
  }

  public async getProfilePicture(attachment: Attachment) {
    if (!attachment?.previewUrl || this.profilePic) return;
    this.profilePic = attachment.previewUrl;
  }

  passwordPatternValidator(control: FormControl): { [key: string]: boolean } | null {
    const password = control.value;

    // Define a regular expression pattern for the password criteria
    const pattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[-@$!%*?&])[A-Za-z\d@$!%*?&-]{8,}$/;

    if (!pattern.test(password)) {
      return { invalidPassword: true };
    }

    return null;
  }

  matchPassword(newPassword: string, confirmNewPassword: string) {
    return (formGroup: FormGroup) => {
      const passwordControl = formGroup.controls[newPassword];
      const confirmPasswordControl = formGroup.controls[confirmNewPassword];

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        confirmPasswordControl.setErrors(null);
        return null;
      }
    };
  }

  confirmPasswordValidator(passwordForm: FormGroup) {
    const newPassword = passwordForm?.value.newPassword ?? '';
    const confirmNewPassword = passwordForm?.value.confirmNewPassword ?? '';
    if (newPassword !== confirmNewPassword) {
      return false;
    }
    return true;
  }
}
